import * as RadixCheckbox from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export interface CheckboxProps {
  checked?: boolean | 'indeterminate';
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean | 'indeterminate') => void;
  disabled?: boolean;
  /** Visible label (sans 13px). Required unless aria-label is given. */
  label?: ReactNode;
  /** Secondary line under the label. */
  description?: ReactNode;
  'aria-label'?: string;
  id?: string;
  name?: string;
  value?: string;
  className?: string;
}

/** 14px square checkbox, 2px radius, brand fill when checked. */
export function Checkbox({ checked, defaultChecked, onCheckedChange, disabled, label, description, id, name, value, className, ...aria }: CheckboxProps) {
  const generated = useId();
  const boxId = id ?? `cb-${generated}`;
  const box = (
    <RadixCheckbox.Root
      id={boxId}
      name={name}
      value={value}
      checked={checked}
      defaultChecked={defaultChecked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={aria['aria-label']}
      className={cn(
        'inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border border-line bg-bg transition-colors hover:border-mute/60 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-brand data-[state=checked]:bg-brand data-[state=indeterminate]:border-brand data-[state=indeterminate]:bg-brand',
        label ? 'mt-0.5' : '',
      )}
    >
      <RadixCheckbox.Indicator className="text-white">
        {checked === 'indeterminate' ? <Minus size={10} strokeWidth={3} aria-hidden /> : <Check size={10} strokeWidth={3} aria-hidden />}
      </RadixCheckbox.Indicator>
    </RadixCheckbox.Root>
  );
  if (!label) return <span className={className}>{box}</span>;
  return (
    <div className={cn('flex items-start gap-2.5', className)}>
      {box}
      <label htmlFor={boxId} className={cn('min-w-0 text-nav leading-snug text-ink', disabled ? 'opacity-50' : 'cursor-pointer')}>
        {label}
        {description ? <span className="mt-0.5 block text-xs text-mute">{description}</span> : null}
      </label>
    </div>
  );
}
